<template>
  <div class="password-component">
    <VeeForm ref="form" class="password-form" @submit="onSaveQuit">
      <IconLabel heavy no-padding :label="$t('ETAB.PAGES.PROFIL.@PASSWORD_INFORMATION')" icon="vpn_key" />
      <!-- Current Password -->
      <Field
        v-slot="{ errors, field, value }"
        v-model="internalAccountPassword[IAccountPassword.currentPassword]"
        as="div"
        :name="$filters.formLabelValidation($t('ETAB.FORMS.LABELS.@ACTUAL_PASSWORD'))"
        :rules="{ required: true }"
      >
        <QInput
          v-bind="field"
          :model-value="value"
          :name="IAccountPassword.currentPassword"
          :type="isActualPasswordPwd ? 'password' : 'text'"
          :error="errors.length > 0"
          :error-message="errors[0]"
          autocomplete="current-password"
          :label="$t('ETAB.FORMS.LABELS.@ACTUAL_PASSWORD')"
        >
          <template #append>
            <QIcon
              :name="isActualPasswordPwd ? 'icon-f-eye' : 'icon-f-eye-off'"
              class="cursor-pointer"
              @click="isActualPasswordPwd = !isActualPasswordPwd"
            />
          </template>
        </QInput>
      </Field>

      <!-- New Password -->
      <Field
        ref="newPassword"
        v-slot="{ errors, field, value }"
        v-model="internalAccountPassword[IAccountPassword.newPassword]"
        as="div"
        :name="$filters.formLabelValidation($t('ETAB.FORMS.LABELS.@NEW_PASSWORD'))"
        :rules="{ required: true }"
      >
        <QInput
          v-bind="field"
          :model-value="value"
          class="q-pb-md"
          :type="isNewPasswordPwd ? 'password' : 'text'"
          :name="IAccountPassword.newPassword"
          :error="errors.length > 0"
          :error-message="errors[0]"
          :label="$t('ETAB.FORMS.LABELS.@NEW_PASSWORD')"
          autocomplete="new-password"
          @update:model-value="setNewPassword"
        >
          <template #append>
            <QIcon :name="isNewPasswordPwd ? 'icon-f-eye' : 'icon-f-eye-off'" class="cursor-pointer" @click="isNewPasswordPwd = !isNewPasswordPwd" />
          </template>
        </QInput>
      </Field>

      <!-- New Password Confirmation -->
      <Field
        ref="newPasswordConfirm"
        v-slot="{ errors, field, value }"
        v-model="internalAccountPassword[IAccountPassword.newPasswordConfirm]"
        as="div"
        :name="$filters.formLabelValidation($t('ETAB.FORMS.LABELS.@CONFIRM_NEW_PASSWORD'))"
        :rules="{ required: true }"
      >
        <QInput
          v-bind="field"
          :model-value="value"
          :type="isNewConfirmPasswordPwd ? 'password' : 'text'"
          :name="IAccountPassword.newPasswordConfirm"
          :error="errors.length > 0"
          :error-message="errors[0]"
          autocomplete="new-password"
          :label="$t('ETAB.FORMS.LABELS.@CONFIRM_NEW_PASSWORD')"
        >
          <template #append>
            <QIcon
              :name="isNewConfirmPasswordPwd ? 'icon-f-eye' : 'icon-f-eye-off'"
              class="cursor-pointer"
              @click="isNewConfirmPasswordPwd = !isNewConfirmPasswordPwd"
            />
          </template>
        </QInput>
      </Field>
      <section class="q-mt-md password-form-security-check">
        <p class="text-bold">
          {{ $t("ETAB.PAGES.PROFIL.@PASSWORD_SECURITY") }}
        </p>
        <div class="password-form-security-check-list">
          <div>
            <div class="password-form-security-check-item">
              <QIcon
                v-if="newPasswordValue.length > 0"
                :name="internalAccountPassword.passwordHasEightCaract() ? 'check_circle' : 'highlight_off'"
                size="md"
                :color="internalAccountPassword.passwordHasEightCaract() ? 'green' : 'red'"
              />
              <p>{{ $t("CORE.COMPONENTS.PASSWORD_SECURITY.@PASSWORD_MIN_CARACT") }}</p>
            </div>
            <div class="password-form-security-check-item">
              <QIcon
                v-if="newPasswordValue.length > 0"
                :name="internalAccountPassword.passwordHasMajCaract() ? 'check_circle' : 'highlight_off'"
                :color="internalAccountPassword.passwordHasMajCaract() ? 'green' : 'red'"
                size="md"
              />
              <p>{{ $t("CORE.COMPONENTS.PASSWORD_SECURITY.@PASSWORD_MAJ") }}</p>
            </div>
          </div>
          <div>
            <div class="password-form-security-check-item">
              <QIcon
                v-if="newPasswordValue.length > 0"
                :name="internalAccountPassword.passwordHasMinCaract() ? 'check_circle' : 'highlight_off'"
                :color="internalAccountPassword.passwordHasMinCaract() ? 'green' : 'red'"
                size="md"
              />
              <p>{{ $t("CORE.COMPONENTS.PASSWORD_SECURITY.@PASSWORD_MIN") }}</p>
            </div>
            <div class="password-form-security-check-item">
              <QIcon
                v-if="newPasswordValue.length > 0"
                :name="internalAccountPassword.passwordHasNumericCaract() ? 'check_circle' : 'highlight_off'"
                :color="internalAccountPassword.passwordHasNumericCaract() ? 'green' : 'red'"
                size="md"
              />
              <p>{{ $t("CORE.COMPONENTS.PASSWORD_SECURITY.@PASSWORD_NUMBER") }}</p>
            </div>
            <div class="password-form-security-check-item">
              <QIcon
                v-if="newPasswordValue.length > 0"
                :name="internalAccountPassword.passwordHasSpecialCaract() ? 'check_circle' : 'highlight_off'"
                :color="internalAccountPassword.passwordHasSpecialCaract() ? 'green' : 'red'"
                size="md"
              />
              <p>{{ $t("CORE.COMPONENTS.PASSWORD_SECURITY.@PASSWORD_SPECIAL_CHAR", { specialChars: PASSWORD_SECURITY_SPECIAL_CHARS }) }}</p>
            </div>
          </div>
        </div>
      </section>
      <QFooter class="bg-transparent footer-padding">
        <div>
          <QBtn
            type="submit"
            color="secondary"
            class="full-width btn-navigation semi-transparent-bottom-shadow"
            :label="$t('ETAB.BUTTONS.@SAVE_QUIT')"
            no-caps
          />
        </div>
      </QFooter>
    </VeeForm>
  </div>
</template>

<script>
  import { Form as VeeForm, Field } from "vee-validate";
  import { AccountPasswordModel, IAccountPassword } from "@core/models";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { PasswordHelpers } from "@core/helpers";
  import { PASSWORD_SECURITY_SPECIAL_CHARS } from "@core/constants";

  export default {
    components: {
      VeeForm,
      Field,
    },
    emits: ["onSaveQuit"],
    data() {
      return {
        internalAccountPassword: new AccountPasswordModel(),
        isActualPasswordPwd: true,
        isNewPasswordPwd: true,
        isNewConfirmPasswordPwd: true,
        newPasswordValue: "",
      };
    },
    created() {
      this.IAccountPassword = IAccountPassword;
      this.PASSWORD_SECURITY_SPECIAL_CHARS = PASSWORD_SECURITY_SPECIAL_CHARS;
    },
    methods: {
      setNewPassword() {
        this.newPasswordValue = this.internalAccountPassword[IAccountPassword.newPassword];
      },
      async onSaveQuit() {
        const newPassword = this.internalAccountPassword.getNewPassword();
        const newPasswordConfirm = this.internalAccountPassword.getNewConfirmPassword();
        if (!PasswordHelpers.hasAllCriterias(newPassword)) {
          this.$refs.newPassword.setErrors([this.$t("ERRORS.@PASSWORD_NOT_ACCORDING_ERROR")]);
          NotificationsManager.error({ message: "ERRORS.@PASSWORD_NOT_ACCORDING" });
        } else if (newPassword !== newPasswordConfirm) {
          this.$refs.newPasswordConfirm.setErrors([this.$t("ERRORS.@PASSWORD_NOT_SIMILAR_ERROR")]);
          NotificationsManager.error({ message: "ERRORS.@PASSWORD_NOT_SIMILAR" });
        } else {
          this.$emit("onSaveQuit", { accountPassword: this.internalAccountPassword });
        }
      },
    },
  };
</script>

<style lang="scss">
  .password-component {
    $space_for_validation: 16px;

    > form > section {
      padding: 15px;

      .password-form-security-check-list {
        display: flex;
        justify-content: space-around;
        flex-wrap: wrap;
        @media screen and (max-width: $breakpoint-sm-max) {
          justify-content: start;
        }
        .password-form-security-check-item {
          display: flex;
          align-items: center;
          > p {
            padding-left: 10px;
            margin-top: 16px;
            align-self: center;
          }
        }
      }
      .q-input {
        margin-top: $space_for_validation;
      }

      .read-only-input {
        margin-top: $space_for_validation;
        label {
          font-weight: bold;
        }
      }
    }
    .password-form-security-check {
      background: white;
      box-shadow: $hard-shadow;
      border-radius: 20px;
      text-align: center;
      overflow: hidden;
    }
  }
</style>
